import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
	CachedSummaryPollerStatusType,
	IAllDateRanges,
	IDataPoints,
	IFilterData,
	ILongitudinalChartData,
	INotesLeftPanelData,
	INotesReferenceData,
	IUnmappedData,
	NotesLevelType,
} from "../models/interfaces";
import { assignColorToTagNotes, initializeTagColorMap } from "src/pages/Insights/transformer";

interface INotesInsightsState {
	isSelectedStudyNotes: boolean;
	notesError: boolean;
	dateRangeSlicer: string;
	allDateRanges: IAllDateRanges;
	notesLoading: {
		leftPanel: boolean;
		longitudinalChart: boolean;
		summary: boolean;
		refreshSummary: boolean;
		referencePane: boolean;
	};

	allLevelsData: INotesLeftPanelData[];
	selectedLevelData: {
		levelToFetch: INotesLeftPanelData | null;
		level1: INotesLeftPanelData | null;
		level2: INotesLeftPanelData | null;
		level3: INotesLeftPanelData | null;
	};
	chartColumnData: string[];
	chartColumnDataColorMap: {
		name: string;
		color: string;
	}[];
	selectedLevelFromSearch: INotesLeftPanelData;
	selectedLevelType: NotesLevelType;
	notesRightPanelReset: number;
	notesFilterDrawerReset: number;

	allFiltersData: IFilterData[];
	selectedFilters: string[];
	allSplitByData: IFilterData[];
	selectedSplitByData: string[];
	isFilterFocused: boolean;
	isSplitByFocused: boolean;

	notesReferences: INotesReferenceData[];
	isNotesReferenceDrawerVisible: boolean;
	pagination: { current: number; pageSize: number; total: number };
	isNotesFilterDrawerOpen: boolean;

	longitudinalChartData: ILongitudinalChartData[];
	unmappedDataCount: IUnmappedData;

	summaryCache: {
		[key: string]: {
			summaryText: string | null;
			cacheId: string | null;
			dataPoints: IDataPoints;
			references: INotesReferenceData[];
		};
	};
	isRefreshVisible: boolean;
	cachedSummaryPollerStatus: CachedSummaryPollerStatusType;
	summaryText: string | null;
	sourceText: string | null;
	cacheId: string | null;
	isSummaryFetchingComplete: boolean;
	abortStreaming: AbortController | null;
	dataPoints: IDataPoints;
	dateRangeDisabled: { [key: string]: boolean };
	isDateAvailable: { [key: string]: boolean };
	highlight: {
		light: string[];
		dark: string[];
	};
}

const initialState: INotesInsightsState = {
	isSelectedStudyNotes: true,
	notesError: false,
	dateRangeSlicer: "6M",
	allDateRanges: {
		"3M": { start: null, end: null },
		"6M": { start: null, end: null },
		"1Yr": { start: null, end: null },
		"2Yr": { start: null, end: null },
		Max: { start: null, end: null },
	},
	notesLoading: {
		leftPanel: true,
		longitudinalChart: true,
		summary: true,
		refreshSummary: false,
		referencePane: false,
	},

	allLevelsData: [],
	selectedLevelData: {
		levelToFetch: null,
		level1: null,
		level2: null,
		level3: null,
	},
	chartColumnData: [],
	chartColumnDataColorMap: [],
	selectedLevelFromSearch: null,
	selectedLevelType: NotesLevelType.L3,
	notesRightPanelReset: 0,
	notesFilterDrawerReset: 0,

	allFiltersData: [],
	selectedFilters: [],
	allSplitByData: [],
	selectedSplitByData: [],
	isFilterFocused: false,
	isSplitByFocused: false,

	notesReferences: [],
	isNotesReferenceDrawerVisible: false,
	pagination: { current: 1, pageSize: 10, total: null },
	isNotesFilterDrawerOpen: false,

	longitudinalChartData: [],
	unmappedDataCount: { total: null, noDate: null, noLabel: null },

	summaryCache: {},
	isRefreshVisible: false,
	cachedSummaryPollerStatus: CachedSummaryPollerStatusType.InProgress,
	summaryText: null,
	sourceText: null,
	cacheId: null,
	abortStreaming: null,
	isSummaryFetchingComplete: false,
	dataPoints: { total: null, filtered: null, missingDates: null },
	dateRangeDisabled: { "3M": false, "6M": false, "1Yr": false, "2Yr": false, Max: false },
	isDateAvailable: { "3M": true, "6M": true, "1Yr": true, "2Yr": true, Max: true },
	highlight: {
		light: [],
		dark: [],
	},
};

const notesInsightsSlice = createSlice({
	name: "notesInsights",
	initialState,
	reducers: {
		setIsSelectedStudyNotes(state, action: PayloadAction<boolean>) {
			state.isSelectedStudyNotes = action.payload;
		},
		setNotesError(state, action: PayloadAction<boolean>) {
			state.notesError = action.payload;
		},
		setDateRangeSlicer(state, action: PayloadAction<string>) {
			state.dateRangeSlicer = action.payload;
		},
		setAllDateRanges(state, action: PayloadAction<IAllDateRanges>) {
			state.allDateRanges = action.payload;
		},
		updateDateRanges(state, action: PayloadAction<Partial<IAllDateRanges>>) {
			state.allDateRanges = { ...state.allDateRanges, ...action.payload };
		},
		updateNotesLoading(state, action: PayloadAction<{ [key: string]: boolean }>) {
			Object.entries(action.payload).forEach(([key, value]) => {
				if (key in state.notesLoading) {
					state.notesLoading[key as keyof INotesInsightsState["notesLoading"]] = value;
				}
			});
			if (action.payload.leftPanel) {
				state.notesLoading.summary = true;
				state.notesLoading.longitudinalChart = true;
			}
		},
		setAllLevelsData(state, action: PayloadAction<INotesLeftPanelData[]>) {
			state.allLevelsData = action.payload;
		},
		setSelectedLevelData(state, action: PayloadAction<Partial<INotesInsightsState["selectedLevelData"]>>) {
			state.selectedLevelData = { ...state.selectedLevelData, ...action.payload };
		},
		setChartColumnData(state, action: PayloadAction<INotesInsightsState["chartColumnData"]>) {
			state.chartColumnData = action.payload;
			const tags = action.payload.map((tag) => ({ name: tag }));
			const tagColorMap = initializeTagColorMap(tags);
			const usedColors = state.chartColumnDataColorMap.map((item) => item.color);
			const colors = [
				"#eeae73",
				"#60a5fa",
				"#4ade80",
				"#f472b6",
				"#fde047",
				"#c084fc",
				"#818cf8",
				"#2dd4bf",
				"#f87171",
				"#9ca3af",
				"#fdba74",
				"#a3e635",
				"#34d399",
				"#22d3ee",
				"#facc15",
				"#e879f9",
			];

			tags.forEach((tag, index) => {
				assignColorToTagNotes(tag.name, tagColorMap, usedColors, colors, index);
			});

			state.chartColumnDataColorMap = Object.entries(tagColorMap).map(([name, color]) => ({ name, color: color as string }));
		},
		setChartColumnDataColorMap(state, action: PayloadAction<INotesInsightsState["chartColumnDataColorMap"]>) {
			state.chartColumnDataColorMap = action.payload;
		},
		setSelectedLevelFromSearch(state, action: PayloadAction<INotesLeftPanelData | null>) {
			state.selectedLevelFromSearch = action.payload;
		},
		setSelectedLevelType(state, action: PayloadAction<NotesLevelType>) {
			state.selectedLevelType = action.payload;
		},
		resetRightPanel(state) {
			state.notesRightPanelReset += 1;
			state.isNotesReferenceDrawerVisible = false;
			state.notesFilterDrawerReset += 1;
		},
		resetNotesFilterDrawer(state) {
			state.notesFilterDrawerReset += 1;
		},
		setAllFiltersData(state, action: PayloadAction<IFilterData[]>) {
			state.allFiltersData = action.payload;
		},
		setSelectedFilters(state, action: PayloadAction<string[]>) {
			state.selectedFilters = action.payload;
		},
		setAllSplitByData(state, action: PayloadAction<IFilterData[]>) {
			state.allSplitByData = action.payload;
		},
		setSelectedSplitByData(state, action: PayloadAction<string[]>) {
			state.selectedSplitByData = action.payload;
		},
		setIsFilterFocused(state, action: PayloadAction<boolean>) {
			state.isFilterFocused = action.payload;
		},
		setIsSplitByFocused(state, action: PayloadAction<boolean>) {
			state.isSplitByFocused = action.payload;
		},
		setNotesReferences(state, action: PayloadAction<INotesReferenceData[]>) {
			state.notesReferences = action.payload;
		},
		setIsNotesReferenceDrawerVisible(state, action: PayloadAction<boolean>) {
			state.isNotesReferenceDrawerVisible = action.payload;
		},
		setPagination(state, action: PayloadAction<{ current: number; pageSize: number; total: number }>) {
			state.pagination = action.payload;
		},
		setLongitudinalChartData(state, action: PayloadAction<ILongitudinalChartData[]>) {
			state.longitudinalChartData = action.payload;
		},
		setUnmappedDataCount(state, action: PayloadAction<IUnmappedData>) {
			state.unmappedDataCount = action.payload;
		},
		setSummaryText(state, action: PayloadAction<string>) {
			state.summaryText = action.payload;
		},
		setSourceText(state, action: PayloadAction<string>) {
			state.sourceText = action.payload;
		},
		setCacheId(state, action: PayloadAction<string>) {
			state.cacheId = action.payload;
		},
		setIsSummaryFetchingComplete(state, action: PayloadAction<boolean>) {
			state.isSummaryFetchingComplete = action.payload;
		},
		setIsRefreshVisible(state, action: PayloadAction<boolean>) {
			state.isRefreshVisible = action.payload;
		},
		setCachedSummaryPollerStatus(state, action: PayloadAction<CachedSummaryPollerStatusType>) {
			state.cachedSummaryPollerStatus = action.payload;
		},
		clearSummaryCache(state) {
			state.summaryCache = {};
		},
		updateSummaryCache(state, action: PayloadAction<{ key: string; value: Partial<INotesInsightsState["summaryCache"][string]> }>) {
			state.summaryCache[action.payload.key] = { ...state.summaryCache[action.payload.key], ...action.payload };
		},
		updateAbortStreaming(state, action: PayloadAction<AbortController | null>) {
			if (state.abortStreaming) {
				try {
					state.abortStreaming.abort();
				} catch (error) {
					console.warn("Failed to abort", error);
				}
			}
			state.abortStreaming = action.payload;
		},
		setDataPoints(state, action: PayloadAction<IDataPoints>) {
			state.dataPoints = action.payload;
		},
		setDateRangeDisabled(state, action: PayloadAction<{ [key: string]: boolean }>) {
			state.dateRangeDisabled = { ...state.dateRangeDisabled, ...action.payload };
		},
		resetDateRangeDisabled(state) {
			state.dateRangeDisabled = { "3M": false, "6M": false, "1Yr": false, "2Yr": false, Max: false };
		},
		setIsDateAvailable(state, action: PayloadAction<{ [key: string]: boolean }>) {
			state.isDateAvailable = { ...state.isDateAvailable, ...action.payload };
		},
		resetIsDateAvailable(state) {
			state.isDateAvailable = { "3M": true, "6M": true, "1Yr": true, "2Yr": true, Max: true };
		},
		toggleNotesFilterDrawer(state, action: PayloadAction<boolean>) {
			if (action.payload) {
				state.isNotesFilterDrawerOpen = action.payload;
			} else {
				state.isNotesFilterDrawerOpen = false;
			}
		},
		updateHighlight(state, action: PayloadAction<Partial<{ light: string[]; dark: string[] }>>) {
			state.highlight = { ...state.highlight, ...action.payload };
		},
	},
});

export const {
	setIsSelectedStudyNotes,
	setNotesError,
	setDateRangeSlicer,
	setAllDateRanges,
	updateDateRanges,
	updateNotesLoading,
	setAllLevelsData,
	setSelectedLevelData,
	setChartColumnData,
	setChartColumnDataColorMap,
	setSelectedLevelFromSearch,
	setSelectedLevelType,
	resetRightPanel,
	resetNotesFilterDrawer,
	setAllFiltersData,
	setSelectedFilters,
	setAllSplitByData,
	setSelectedSplitByData,
	setIsFilterFocused,
	setIsSplitByFocused,
	setNotesReferences,
	setIsNotesReferenceDrawerVisible,
	setPagination,
	setLongitudinalChartData,
	setUnmappedDataCount,
	setSummaryText,
	setSourceText,
	setCacheId,
	setIsSummaryFetchingComplete,
	setIsRefreshVisible,
	setCachedSummaryPollerStatus,
	clearSummaryCache,
	updateSummaryCache,
	updateAbortStreaming,
	setDataPoints,
	setDateRangeDisabled,
	resetDateRangeDisabled,
	setIsDateAvailable,
	resetIsDateAvailable,
	toggleNotesFilterDrawer,
	updateHighlight,
} = notesInsightsSlice.actions;

export default notesInsightsSlice.reducer;
